import { get } from 'svelte/store';

import { selectedIndustryCentroids } from '$lib/stores/data';
import { cantons, companySizeRange, languages } from '$lib/stores/selection';
import type { Axis, Suggestions } from '$lib/profile/suggestions';

/**
 * Writing the accepted findings into the filters everything else reads.
 *
 * # Nothing here holds a filter of its own
 *
 * The same rule the rest of the guide follows: the profile screen writes into
 * `selectedIndustryCentroids`, `companySizeRange`, `languages` and `cantons`,
 * and that is the whole of the handover. The filter panel edits them, the price
 * bar counts them, the search runs them — none of which needs to know a profile
 * happened.
 *
 * It is also what makes "let the customer change anything the analysis set"
 * free. There is no draft to reconcile: a customer who unticks a category on
 * the profile screen and one who removes it in the sidebar afterwards are doing
 * the same thing to the same store.
 */

/** Apply the chosen axes, and clear the ones that were not chosen. */
export function applySuggestions(suggestions: Suggestions, chosen: Axis[]): void {
  const wanted = new Set(chosen);

  applyIndustry(suggestions, wanted.has('industry'));
  applySize(suggestions, wanted.has('size'));
  applyRegion(suggestions, wanted.has('region'));
}

/**
 * Clearing is as important as setting.
 *
 * The screen is re-applied whenever a tick changes, so an axis that has just
 * been turned off has to be removed rather than left behind. Without that,
 * unticking would leave the filter in place and the count unchanged, which
 * reads as the screen being broken.
 *
 * Only what this screen set is cleared, and only when the axis was on offer.
 * A customer who arrived with cantons already chosen keeps them.
 */
function applyIndustry(suggestions: Suggestions, on: boolean): void {
  if (!suggestions.industry) return;

  if (!on) {
    selectedIndustryCentroids.set(new Map());
    return;
  }

  // A Map of id to name, which is what the tree and the filter bar read. The
  // ids are the raw centroid ids the profile returned; they name a category,
  // not a company, and the centroid listing already publishes them.
  const chosen = new Map<string, string>();
  for (const centroid of suggestions.industry.centroids) {
    chosen.set(String(centroid.id), centroid.name);
  }
  selectedIndustryCentroids.set(chosen);
}

function applySize(suggestions: Suggestions, on: boolean): void {
  if (!suggestions.size) return;

  if (!on) {
    companySizeRange.set([null, null]);
    return;
  }

  // A floor with an open top, in the usual case. See sizeCeiling.
  companySizeRange.set([suggestions.size.min, suggestions.size.max]);
}

function applyRegion(suggestions: Suggestions, on: boolean): void {
  if (!suggestions.region) return;

  if (!on) {
    languages.set([]);
    cantons.set([]);
    return;
  }

  languages.set([...suggestions.region.languages]);

  // Cantons only when one actually stood out inside the served regions. The
  // usual answer is that none did, and writing every canton of the
  // Deutschschweiz would say the same thing as the language filter at ten times
  // the length — and would silently drop a company whose postal code resolves
  // to no canton.
  cantons.set([...suggestions.region.cantons]);
}

/** Whether the filters currently match what an axis would set. */
export function isApplied(suggestions: Suggestions, axis: Axis): boolean {
  switch (axis) {
    case 'industry':
      return suggestions.industry !== null && get(selectedIndustryCentroids).size > 0;
    case 'size':
      return suggestions.size !== null && get(companySizeRange)[0] !== null;
    case 'region':
      return suggestions.region !== null && get(languages).length > 0;
  }
}
